import { motion } from "framer-motion";
import { RefreshCw, HeartCrack } from "lucide-react";
import { FloatingMoon, FloatingCloud, SleepZ } from "./SleepDecorations";

const CARDS = [
  {
    icon: RefreshCw,
    title: "Por que os despertares não param sozinhos",
    bullets: [
      "Seu filho não acorda por ser \"agitado\". Ele aprendeu a associar o sono ao colo, peito ou embalo.",
      "A cada troca de ciclo, ele busca a mesma condição inicial para voltar a dormir.",
      "Sem ensinar outra forma de iniciar o sono, os despertares se repetem noite após noite.",
    ],
  },
  {
    icon: HeartCrack,
    title: "O que o cansaço crônico faz com você",
    bullets: [
      "A paciência some primeiro. Depois a concentração. Depois o prazer em qualquer coisa.",
      "O corpo funciona no automático, mas a alegria de ser mãe vai embora aos poucos.",
      "Você não está pedindo muito. Você só quer dormir e acordar bem.",
    ],
  },
];

export default function PainSection() {
  return (
    <section className="relative py-28 lg:py-36 overflow-hidden bg-[#3d2a5a]">
      <div className="absolute inset-0 stars-bg opacity-20 pointer-events-none" />
      <FloatingMoon
        className="absolute top-32 right-[-40px]"
        size={220}
        tone="light"
      />
      <FloatingCloud
        className="absolute top-1/2 -left-12 text-[#a78bfa]/30 drift-slow"
        size={180}
      />
      <SleepZ
        className="absolute bottom-32 right-[12%] text-[#6d28d9]/70"
        size={60}
      />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        {/* Centered Headline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] text-white leading-[1.08]">
            O bebê não é o problema.
            <span className="block text-[#a78bfa] font-medium mt-3">
              O problema é o que acontece com você
            </span>
            <span className="block text-[#a78bfa] font-medium">
              enquanto isso.
            </span>
          </h2>
        </motion.div>

        {/* Tight emotional copy (no widows, narrower) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-2xl mx-auto space-y-6 text-slate-200/90 text-[16px] lg:text-[17px] leading-[1.8] font-light text-center"
        >
          <p>
            São 2h da manhã. Você levanta pela terceira vez. Pega o bebê no
            colo, balança, amamenta, espera ele fechar os olhos. Ele abre os
            olhos. Você começa tudo de novo.
          </p>
          <p>
            E aí vem aquele pensamento que você não conta pra ninguém:{" "}
            <em className="text-violet-200 not-italic font-medium">
              &quot;Eu amo ele. Mas eu não aguento mais.&quot;
            </em>
          </p>
          <p className="text-violet-200/95 font-medium">
            A exaustão de mãe não é fraqueza. É o resultado direto de um ciclo
            que ninguém te ensinou a quebrar.
          </p>
        </motion.div>

        {/* Visual cards: Icon + Title + 3 bullets */}
        <div className="mt-20 grid lg:grid-cols-2 gap-6 lg:gap-7">
          {CARDS.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 lg:p-10 backdrop-blur-sm flex flex-col"
            >
              <div className="w-14 h-14 rounded-2xl bg-violet-500/15 border border-violet-400/20 flex items-center justify-center mb-6">
                <c.icon
                  className="w-6 h-6 text-[#c4b5fd]"
                  strokeWidth={1.75}
                />
              </div>
              <h3 className="font-display text-2xl lg:text-[26px] text-white mb-6 leading-tight">
                {c.title}
              </h3>
              <ul className="space-y-3.5">
                {c.bullets.map((b, idx) => (
                  <li
                    key={idx}
                    className="flex gap-3 text-slate-200/85 text-[14.5px] leading-[1.65]"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#a78bfa] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
